import { format, parseISO } from "date-fns";
import {
    OutstandingCashItem,
    TransactionForReconciliationFromAccount,
    TransactionForReconciliationFromStatement,
    TransactionsPairForReconciliation,
} from "@common/contracts/reconciliation.contract";
import { fullDateFullYearFormat } from "@common/helpers/date";
import {
    BANK_CASH_ITEM_TYPE_LABELS,
    BankCashItemType,
} from "@common/types/bankCashItem";

interface OutstandingCashItemsInPairsParams {
    outstandingCashItems: OutstandingCashItem[];
    transactionsPairs: TransactionsPairForReconciliation[];
}

interface CanCashItemClearOnDateParams {
    cashItemDateOnly: string;
    clearedDateOnly: string | null | undefined;
}

interface GetCashItemInReconciledPeriodMessageParams {
    type: BankCashItemType;
    lastReconciledClosingDate: string;
}

interface GetCashItemClearedBeforeDateMessageParams {
    type?: BankCashItemType;
    cashItemDateOnly: string;
}

export function getCashItemLabel(type?: BankCashItemType): string {
    return BANK_CASH_ITEM_TYPE_LABELS[
        type ?? BankCashItemType.CHECK
    ].toLowerCase();
}

export function getTransactionDatedBeforeCashItemMessage(
    type?: BankCashItemType,
): string {
    return `Transaction is dated before the ${getCashItemLabel(type)}`;
}

export function getCashItemClearedBeforeDateMessage({
    type,
    cashItemDateOnly,
}: GetCashItemClearedBeforeDateMessageParams): string {
    const formattedDate = format(
        parseISO(cashItemDateOnly),
        fullDateFullYearFormat,
    );
    return `A ${getCashItemLabel(type)} can't clear before its date (${formattedDate})`;
}

export function canCashItemClearOnDate({
    cashItemDateOnly,
    clearedDateOnly,
}: CanCashItemClearOnDateParams): boolean {
    return clearedDateOnly != null && clearedDateOnly >= cashItemDateOnly;
}

export function getCashItemInReconciledPeriodMessage({
    type,
    lastReconciledClosingDate,
}: GetCashItemInReconciledPeriodMessageParams): string {
    const formattedDate = format(
        parseISO(lastReconciledClosingDate),
        fullDateFullYearFormat,
    );
    return `This period is reconciled through ${formattedDate}. Undo that reconciliation before you record a ${getCashItemLabel(type)} dated in it.`;
}

function withoutTransactionIds(
    outstandingCashItems: OutstandingCashItem[],
    transactionIds: number[],
): OutstandingCashItem[] {
    const excludedIds = new Set(transactionIds);
    return outstandingCashItems.filter(
        ({ financialTransactionId }) =>
            !excludedIds.has(financialTransactionId),
    );
}

export function cashItemIdsClearedByStatementLines(
    transactionsPairs: TransactionsPairForReconciliation[],
): number[] {
    return transactionsPairs.flatMap(({ fromStatement, fromAccount }) =>
        fromStatement && fromAccount?.isCashItem
            ? [fromAccount.financialTransactionId]
            : [],
    );
}

export function withoutCashItemsClearedByStatementLines({
    outstandingCashItems,
    transactionsPairs,
}: OutstandingCashItemsInPairsParams): OutstandingCashItem[] {
    return withoutTransactionIds(
        outstandingCashItems,
        cashItemIdsClearedByStatementLines(transactionsPairs),
    );
}

export function canTakeStatementDate(
    transactionFromAccount: TransactionForReconciliationFromAccount,
    transactionFromStatement: TransactionForReconciliationFromStatement,
): boolean {
    return (
        !transactionFromAccount.isCashItem &&
        transactionFromStatement.date !== transactionFromAccount.date
    );
}
